// src/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import './BlogPost.css'; // Import the CSS file for the BlogPost page

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentMessages, setCommentMessages] = useState('');
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState({});
  const [replyMessages, setReplyMessages] = useState({});
  const [showReplyInput, setShowReplyInput] = useState({});
  const [likeMessages, setLikeMessages] = useState('');

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const response = await axiosInstance.get(`/blog/${id}`);
        setBlogPost(response.data);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await axiosInstance.get(`/blog/${id}/comments`);
        setComments(response.data);
      } catch (err) {
        console.error('Failed to fetch comments:', err);
      }
    };

    fetchComments();
  }, [id]);

  const handleLikeBlogPost = async () => {
    try {
      const response = await axiosInstance.post(`/blog/${id}/like`);
      if (response.status === 200) {
        setBlogPost((prev) => ({ ...prev, likes: response.data.likes }));
        setLikeMessages(response.data.message);
      }
    } catch (err) {
      console.error('Failed to like/unlike blog post:', err);
      if (err.response && err.response.status === 400) {
        setLikeMessages('Please login to like');
      } else if (err.response && err.response.data && err.response.data.name === 'TokenExpiredError') {
        setLikeMessages('Please login');
      } else {
        setLikeMessages('Failed to like/unlike');
      }
    }
  };

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post(`/blog/${id}/comment`, { text: commentText });
      if (response.status === 201) {
        setCommentMessages('Comment added successfully');
        setCommentText('');
        // Re-fetch comments to update the UI
        const commentsResponse = await axiosInstance.get(`/blog/${id}/comments`);
        setComments(commentsResponse.data);
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
      if (err.response) {
        if (err.response.status === 401 || (err.response.data && err.response.data.name === 'TokenExpiredError')) {
          setCommentMessages('Please login to add a comment');
        } else {
          setCommentMessages('Failed to add comment');
        }
      } else {
        setCommentMessages('Failed to add comment');
      }
    }
  };

  const handleReplyChange = (e, commentId) => {
    setReplyText({ ...replyText, [commentId]: e.target.value });
  };

  const handleReplySubmit = async (e, commentId) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post(`/blog/comment/${commentId}/reply`, { text: replyText[commentId] });
      if (response.status === 201) {
        setReplyMessages((prev) => ({ ...prev, [commentId]: 'Reply added successfully' }));
        setReplyText({ ...replyText, [commentId]: '' });
        // Re-fetch replies to update the UI
        const repliesResponse = await axiosInstance.get(`/blog/comment/${commentId}/replies`);
        setReplies((prev) => ({ ...prev, [commentId]: repliesResponse.data }));
      }
    } catch (err) {
      console.error('Failed to add reply:', err);
      if (err.response) {
        if (err.response.status === 401 || (err.response.data && err.response.data.name === 'TokenExpiredError')) {
          setReplyMessages((prev) => ({ ...prev, [commentId]: 'Please login to add a reply' }));
        } else {
          setReplyMessages((prev) => ({ ...prev, [commentId]: 'Failed to add reply' }));
        }
      } else {
        setReplyMessages((prev) => ({ ...prev, [commentId]: 'Failed to add reply' }));
      }
    }
  };

  const fetchReplies = async (commentId) => {
    try {
      const response = await axiosInstance.get(`/blog/comment/${commentId}/replies`);
      setReplies((prev) => ({ ...prev, [commentId]: response.data }));
    } catch (err) {
      console.error('Failed to fetch replies:', err);
    }
  };

  const toggleReplyInput = (commentId) => {
    setShowReplyInput((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
    if (!showReplyInput[commentId]) {
      fetchReplies(commentId);
    }
  };

  const commentFormStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px',
  };

  const commentInputStyle = {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '8px',
  };

  const commentButtonStyle = {
    backgroundColor: '#ff7c0a',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    textDecoration: 'none',
    transition: 'background-color 0.3s',
    alignSelf: 'flex-start',
  };

  const replyStyle = {
    marginLeft: '16px',
    marginTop: '8px',
  };

  const replyFormStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
  };

  const replyInputStyle = {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '8px',
  };

  const replyButtonStyle = {
    backgroundColor: '#ff7c0a',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    textDecoration: 'none',
    transition: 'background-color 0.3s',
    alignSelf: 'flex-start',
  };

  const messageStyle = {
    color: '#ff7c0a',
    marginTop: '8px',
  };

  return (
    <section className="blog-post-section">
      <button className="back-button" onClick={() => navigate('/blog')}>
        ← Back to Blog
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p>{error}</p>
      ) : blogPost ? (
        <div className="blog-post-container">
          <h2 className="blog-post-title">{blogPost.title}</h2>
          {blogPost.subtitle && <h3 className="blog-post-subtitle">{blogPost.subtitle}</h3>}
          {blogPost.image && <img src={blogPost.image} alt={blogPost.title} className="blog-post-image" />}
          <p className="blog-post-body">{blogPost.body}</p>
          <div className="blog-post-meta">
            <span className="blog-open-style" onClick={handleLikeBlogPost}>👍 {blogPost.likes}</span>
            <span>💬 {blogPost.comments}</span>
          </div>
          {likeMessages && <p style={messageStyle}>{likeMessages}</p>}
          <div className="comments-section">
            <h4>Comments</h4>
            {Array.isArray(comments) && comments.length > 0 ? (
              comments.map((comment, index) => (
                <div key={index}>
                  <p><strong>{comment.User?.first_name}:</strong> {comment.text}</p>
                  <button onClick={() => toggleReplyInput(comment.id)} className="reply-text">Replies</button>
                  {showReplyInput[comment.id] && (
                    <div style={replyStyle}>
                      {Array.isArray(replies[comment.id]) && replies[comment.id].length > 0 ? (
                        replies[comment.id].map((reply, index) => (
                          <p key={index}><strong>{reply.User?.first_name}:</strong> {reply.text}</p>
                        ))
                      ) : (
                        <p>No replies yet</p>
                      )}
                      <form style={replyFormStyle} onSubmit={(e) => handleReplySubmit(e, comment.id)}>
                        <input
                          type="text"
                          placeholder="Add a reply"
                          value={replyText[comment.id] || ''}
                          onChange={(e) => handleReplyChange(e, comment.id)}
                          style={replyInputStyle}
                        />
                        <button type="submit" style={replyButtonStyle}>Reply</button>
                      </form>
                      {replyMessages[comment.id] && <p style={messageStyle}>{replyMessages[comment.id]}</p>}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No comments yet</p>
            )}
            <form style={commentFormStyle} onSubmit={handleCommentSubmit}>
              <input
                type="text"
                placeholder="Add a comment"
                value={commentText}
                onChange={handleCommentChange}
                style={commentInputStyle}
              />
              <button type="submit" style={commentButtonStyle}>Comment</button>
            </form>
            {commentMessages && <p style={messageStyle}>{commentMessages}</p>}
          </div>
        </div>
      ) : (
        <p>Blog post not found</p>
      )}
    </section>
  );
};

export default BlogPost;